import { toggle } from "@otto-ec/global-resources/toggle";
import {
  catchFailure,
  flatMap,
  fromCondition,
  pipe,
  succeed,
  pipeMap,
  tapLeft,
  tapRight,
  mapLeft,
  mapRight,
  trySync,
  mapErrorPassThrough,
  fromBoolean,
} from "@otto-ec/assets-core-utils/nano-effect";
import { onLoad } from "@otto-ec/global-resources/dom";
import {
  scope as navigationScope,
  paramsMap,
  stateMap,
  type NavigationParameters,
  type NavigationScope,
  type ParametersDefinition,
  type StateDefinition,
} from "@otto-ec/global-resources/navigation";
import type { Unset } from "@otto-ec/global-resources/nexus";
import type { Unsubscriber } from "svelte/store";
import { sheetScope } from "./utils.js";
import { create } from "../SheetV1.api.js";
import type { OcSheetV1Props } from "../SheetV1.types.g.js";
import { next, close, sheetHistory, initHistoryStack, openSheetProps } from "./historyStack.js";
import { CLOSE_TYPES, CLOSED_WITH_NAVIGATION } from "./events.svelte.js";

/**
 *
 *
 *
 */

const sheetTagName = "oc-sheet-v1" as const;
const useHistoryToggle = [
  "oc_sheet_v1_use_navigation_history",
  import.meta.env.STORYBOOK ?? false,
] as const;

const logNav = /*           */ sheetScope.scope("navigation");
const logParams = /*           */ logNav.scope("hash-handler");
const logUpdater = /*           */ logNav.scope("history-updater");
const logState = /*           */ logNav.scope("state-handler");

const statePropOnLoad = "openOnLoad" as const;
const statePropStack = "historyStack" as const;
const statePropConfig = "currentConfig" as const;
const statePropId = "currentId" as const;

/**
 *
 *
 */
export type SheetHistoryState = {
  /**
 *
 */
  [statePropStack]?: string[];

  /**
 *
 *
 */
  [statePropOnLoad]?: boolean;

  /**
 *
 */
  [statePropId]?: string;

  /**
 *
 *
 */
  [statePropConfig]?: OcSheetV1Props;
};

/**
 *
 */
export type SheetNavScope = NavigationScope<
  typeof sheetTagName,
  {
    hash: ParametersDefinition<{ [sheetTagName]: string }, true>;
    state: StateDefinition<SheetHistoryState>;
  }
>;

/**
 *
 */
const parseB64 = trySync(atob, mapErrorPassThrough<Error>);

/**
 *
 */
const parseJsonToSheetParams = trySync(JSON.parse<OcSheetV1Props>, mapErrorPassThrough<Error>);

const makeSheetPropsOpen = (
  ...props: (Partial<OcSheetV1Props> | undefined | null)[]
): Omit<OcSheetV1Props, "open"> & { open: true } => Object.assign({}, ...props, { open: true });

/**
 *
 *
 */
export const handleIncommingHashChange: (
  curr: NavigationParameters<{ [sheetTagName]: string }>,
  prev: NavigationParameters<{ [sheetTagName]: string }> | undefined,
  scope: SheetNavScope,
) => void = (curr, prev, scope) => {
  if (import.meta.env.DEV)
    logParams.info("scope params schanged", curr.toString(), prev?.toString(), history.length);

  pipe(
    /*                                             */
    /*                                                       */
    /*                                                               */
    fromCondition(
      !!curr.get(sheetTagName),
      curr.get(sheetTagName)!,
      prev?.get(sheetTagName) ?? null,
    ),

    /*                                                                      */
    /*                              */
    mapLeft((prevHash) => {
      if (import.meta.env.DEV)
        logParams.info("hash param is empty, check if a sheet must be closed");

      /*                                                      */
      if (prevHash) close(CLOSE_TYPES[CLOSED_WITH_NAVIGATION]);

      /*                                                            */
      return null;
    }),

    /*                                                     */
    /*                                                                                 */
    flatMap((maybeB64Encoded) =>
      pipe(
        parseB64(maybeB64Encoded),
        catchFailure(() => succeed(maybeB64Encoded)),
      ),
    ),

    /*                                                                            */
    /*                                                                                          */
    flatMap((maybeJson) =>
      pipe(
        parseJsonToSheetParams(maybeJson),
        catchFailure(() => succeed(maybeJson)),
      ),
    ),

    /*                                   */
    pipeMap(
      /*                                                                             */
      /*                              */
      /*                                                                          */
      flatMap((stringOrProps) =>
        fromCondition(
          typeof stringOrProps === "string",
          stringOrProps as string,
          stringOrProps as OcSheetV1Props,
        ),
      ),

      /*                                         */
      /*                                                                          */
      tapRight((sheetId) => {
        if (import.meta.env.DEV) logParams.info("sheet id found, open with id", sheetId);
        const sheetInstance = document.querySelector<HTMLOcSheetV1Element>(`#${sheetId}`);

        /*                                                                    */
        if (sheetInstance)
          return next(sheetId, makeSheetPropsOpen(scope.state().get(statePropConfig)));
        logParams.warn("sheet instance", sheetId, "not found, can not create with id only");
      }),

      /*                                                */
      /*                                                                          */
      tapLeft((sheetProps) => {
        if (import.meta.env.DEV) logParams.info("sheet props found, open with props", sheetProps);

        /*                                                                          */
        /*                                                                                 */
        create(makeSheetPropsOpen(scope.state().get(statePropConfig), sheetProps));
      }),
    ),
  );
};

/**
 *
 *
 *
 */
export const handleIncommingStackChange: (
  curr: string[],
  prev: string[],
  scope: SheetNavScope,
) => void = (curr, prev, scope): void => {
  if (import.meta.env.DEV) logUpdater.info("sheet stack changed", curr, prev, history.length);

  pipe(
    /*                                                                                              */
    /*                                                                              */
    /*                                                                              */

    /*                                                     */
    /*                                                             */
    fromCondition(curr.length < prev.length, prev.length - curr.length, curr.at(-1)),

    /*                       */
    mapRight((entriesToRewind) => {
      /*                                                                       */
      if (entriesToRewind < 1) {
        if (import.meta.env.DEV) logUpdater.info("no entries to rewind, skip history rewind");
        return;
      }

      /*                                                                       */
      /*                                                              */
      if (!scope.hash().get(sheetTagName)) {
        if (import.meta.env.DEV) logUpdater.info("no sheet id in hash, skip history rewind");
        return;
      }

      /*                                               */
      scope.go(-entriesToRewind);
    }),

    /*                                                                                     */
    mapLeft((newSheetId) => {
      /*                                                                                     */
      /*                                                                  */
      if (!newSheetId) {
        if (import.meta.env.DEV) logUpdater.info("no new sheet id, skip pushing to history");
        return;
      }

      /*                                                                          */
      if (scope.hash().get(sheetTagName) === newSheetId) {
        if (import.meta.env.DEV)
          logUpdater.info("sheet id", newSheetId, "is already in the hash, skip pushing");
        return;
      }

      /*                                                             */
      if (import.meta.env.DEV) logUpdater.info("new sheet id", newSheetId, "pushing to history");

      /*                              */
      const hash = scope.hash();
      const state = scope.state();

      pipe(
        fromBoolean(
          /*                                                                   */
          (state.get(statePropOnLoad) && !state.get(statePropId)) ||
            /*                                                 */
            state.get(statePropId) === newSheetId,
          /*                                               */
        ),

        /*                                                          */
        tapRight(() => {
          if (import.meta.env.DEV)
            logUpdater.info("updating state with new sheet id", newSheetId, history.length);

          state.set(statePropId, newSheetId);
          state.set(statePropStack, curr);
          scope.replace();
        }),

        /*                                                     */
        tapLeft(() => {
          if (import.meta.env.DEV)
            logUpdater.info(
              "pushing new history entry with new sheet id",
              newSheetId,
              history.length,
            );

          hash.set(sheetTagName, newSheetId);
          scope.state({ [statePropStack]: curr, [statePropId]: newSheetId });
          scope.push();
        }),
      );
    }),
  );
};

/**
 *
 *
 */
export const initQueryParams: (scope: SheetNavScope) => Unset = (scope) => {
  if (import.meta.env.DEV) logParams.info("init query params integration", history.length);
  return scope.currentHash.sub((curr, prev) => handleIncommingHashChange(curr, prev, scope));
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const prepareOpenSheetOnPageEntry = (scope: SheetNavScope): void => {
  if (import.meta.env.DEV) logUpdater.info("prepare sheet state for page entry", history.length);

  const hash = scope.hash();
  const state = scope.state();

  /*                                                                                 */
  /*                                                                                    */

  /*                                                                                 */
  /*                                                                                      */
  /*                                                    */

  if (hash.has(sheetTagName) && state.empty) {
    /*                                                                                    */
    /*                                                           */
    if (import.meta.env.DEV)
      logParams.info("ensure empty history entry for page entry", history.length);

    const params = hash.get(sheetTagName)!;
    hash.clear();
    scope.replace();

    /*                                                                       */
    /*                                                      */
    if (import.meta.env.DEV)
      logParams.info("push new history entry with params", params, history.length);

    hash.set(sheetTagName, params);
    state.set(statePropOnLoad, true);
    scope.push();
  }
};

export const restoreHistoryStack = (scope: SheetNavScope): void => {
  if (import.meta.env.DEV) logUpdater.info("restore history stack", history.length);
  const state = scope.state();

  /*                                                                                          */
  /*                    */
  if (!state.empty) {
    if (import.meta.env.DEV)
      logUpdater.info("initializing history stack from state", state.get(statePropStack));
    initHistoryStack(state.get("historyStack", []));
  }
};

/**
 *
 *
 *
 *
 */
export const initHistoryHandling: (scope: SheetNavScope) => Unsubscriber = (scope) => {
  if (import.meta.env.DEV) logUpdater.info("init history updater integration", history.length);
  return sheetHistory.subscribe(([curr, prev]) => handleIncommingStackChange(curr, prev, scope));
};

/**
 *
 *
 *
 *
 */
export const initStatePropsUpdater: (scope: SheetNavScope) => Unsubscriber = (scope) => {
  if (import.meta.env.DEV) logState.info("init state handling integration", history.length);

  /*                                                                                */
  /*                                          */
  return openSheetProps.subscribe((props) => {
    if (import.meta.env.DEV) logState.info("open sheet props changed", props, history.length);
    const state = scope.state();

    /*                                                                  */
    if (state.empty || !props) {
      if (import.meta.env.DEV)
        logState.info("no sheet state in scope, or empty props, skip update");
      return;
    }

    /*                                                   */
    /*                                                                  */
    if (import.meta.env.DEV) logState.info("updating state config", props);
    state.set(statePropConfig, props);
    scope.replace();
  });
};

/**
 *
 *
 *
 */
export function useGlobalNavigation(): Unsubscriber {
  /*                                                                    */
  /*                                                                */
  if (import.meta.env.STORYBOOK && window.parent !== window) {
    logNav.info("global navigation integration is disabled in storybook iframe");
    return () => undefined;
  }

  if (import.meta.env.DEV) logNav.info("init global navigation integration");

  /*                                     */
  const unsubs: (Unset | Unsubscriber)[] = [];

  /*                             */
  onLoad.sub(
    async () => {
      const sheetNavScope: SheetNavScope = await navigationScope(sheetTagName, {
        hash: paramsMap([sheetTagName]),
        state: stateMap<SheetHistoryState>(),
      });

      /*                             */
      if (toggle.get(...useHistoryToggle)) {
        /*                                                                          */
        restoreHistoryStack(sheetNavScope);
        prepareOpenSheetOnPageEntry(sheetNavScope);

        unsubs.push(initHistoryHandling(sheetNavScope), initStatePropsUpdater(sheetNavScope));
      } else if (import.meta.env.DEV)
        logNav.info("history updater integration is disabled by toggle:", useHistoryToggle[0]);

      /*                                                                                 */
      unsubs.push(initQueryParams(sheetNavScope));
    },
    { once: true },
  );

  return () => unsubs.forEach((u) => u());
}
